import { useState, useEffect } from 'react';
import { toast } from 'sonner';

export function useDataManagement({ api, isAuthenticated, createBackup }) {
  // Backup state
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [showRestoreBackup, setShowRestoreBackup] = useState(false);
  const [backupList, setBackupList] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(false);

  // GDPR / Data Privacy state
  const [downloadingData, setDownloadingData] = useState(false);
  const [emailingPolicy, setEmailingPolicy] = useState(false);
  const [deletingData, setDeletingData] = useState(false);
  const [dataSummary, setDataSummary] = useState(null);
  const [loadingDataSummary, setLoadingDataSummary] = useState(false);

  // Mealtimes configuration state
  const [userMealTimes, setUserMealTimes] = useState([
    { id: 'breakfast', name: 'Breakfast', icon: '\u{1F305}', enabled: true },
    { id: 'lunch', name: 'Lunch', icon: '\u{2600}\u{FE0F}', enabled: true },
    { id: 'dinner', name: 'Dinner', icon: '\u{1F319}', enabled: true },
    { id: 'snack', name: 'Snack', icon: '\u{1F34E}', enabled: false },
  ]);
  const [customMealName, setCustomMealName] = useState('');
  const [savingMealTimes, setSavingMealTimes] = useState(false);

  // Payment history state
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  const API_URL = '';

  // Trigger daily auto-backup on app load
  useEffect(() => {
    if (isAuthenticated) {
      const timer = setTimeout(() => { createBackup(api, true); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, createBackup, api]);

  // Load user's meal times configuration
  useEffect(() => {
    const loadMealTimes = async () => {
      const savedMealTimes = localStorage.getItem('gym_notebook_meal_times');
      if (savedMealTimes) {
        try { setUserMealTimes(JSON.parse(savedMealTimes)); } catch (e) { /* ignore */ }
      }
      if (isAuthenticated) {
        try {
          const response = await api.get('/data/meal-times');
          if (response.mealTimes) {
            setUserMealTimes(response.mealTimes);
            localStorage.setItem('gym_notebook_meal_times', JSON.stringify(response.mealTimes));
          }
        } catch (e) { console.log('Error loading meal times from backend:', e.message); }
      }
    };
    loadMealTimes();
  }, [isAuthenticated, api]);

  // Backup functions
  const loadBackupStatus = async () => {
    try {
      const data = await api.get('/backup/list');
      if (data?.last_backup) setLastBackup(data.last_backup);
    } catch (err) { console.log('Could not load backup status'); }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const result = await api.post('/backup/create', {});
      toast.success('Backup created!');
      setLastBackup(result.date);
    } catch (err) {
      toast.error('Failed to create backup: ' + (err.message || 'Unknown error'));
    } finally { setCreatingBackup(false); }
  };

  const loadBackupList = async () => {
    setLoadingBackups(true);
    try {
      const result = await api.get('/backup/list');
      setBackupList(result.backups || []);
    } catch (err) { toast.error('Failed to load backups'); }
    finally { setLoadingBackups(false); }
  };

  const handleRestoreBackup = async (backupDate) => {
    if (!window.confirm(`Restore data from ${backupDate}? This will replace your current data.`)) return;
    setRestoringBackup(true);
    try {
      await api.post(`/backup/restore/${backupDate}`, {});
      toast.success(`Data restored from ${backupDate}!`);
      setShowRestoreBackup(false);
      window.location.reload();
    } catch (err) {
      toast.error('Failed to restore backup: ' + (err.message || 'Unknown error'));
    } finally { setRestoringBackup(false); }
  };

  const handleRestoreFromS3 = async () => {
    if (!window.confirm('Restore all your data from cloud backup?\n\nThis will merge your S3 backup with your current data. No existing data will be deleted.')) return;
    setRestoringBackup(true);
    try {
      const summary = await api.get('/data/s3-backup-summary');
      if (!summary?.summary?.total_files || summary.summary.total_files === 0) {
        toast.error('No backup data found in cloud storage');
        return;
      }
      const result = await api.post('/data/restore-from-s3', {});
      if (result.success) {
        const restored = result.restored;
        const restoredItems = [];
        if (restored.nutrition_goals) restoredItems.push('nutrition goals');
        if (restored.body_info) restoredItems.push('body info');
        if (restored.saved_meals > 0) restoredItems.push(`${restored.saved_meals} meals`);
        if (restored.saved_barcodes > 0) restoredItems.push(`${restored.saved_barcodes} barcodes`);
        if (restored.walking_sessions > 0) restoredItems.push(`${restored.walking_sessions} sessions`);
        if (restored.nutrition_days > 0) restoredItems.push(`${restored.nutrition_days} nutrition days`);
        if (restored.walking_days > 0) restoredItems.push(`${restored.walking_days} walking days`);
        if (restored.ai_plans > 0) restoredItems.push(`${restored.ai_plans} AI plans`);
        toast.success(restoredItems.length > 0 ? `Restored: ${restoredItems.join(', ')}` : 'Data restored from cloud!');
        setTimeout(() => { window.location.reload(); }, 1500);
      }
    } catch (err) {
      toast.error('Failed to restore from cloud: ' + (err.message || 'Unknown error'));
    } finally { setRestoringBackup(false); }
  };

  useEffect(() => {
    if (showRestoreBackup) loadBackupList();
  }, [showRestoreBackup]);

  useEffect(() => {
    if (isAuthenticated) loadBackupStatus();
  }, [isAuthenticated]);

  // GDPR functions
  const loadDataSummary = async () => {
    setLoadingDataSummary(true);
    try {
      const result = await api.get('/gdpr/data-summary');
      setDataSummary(result.summary);
    } catch (err) { console.log('Could not load data summary'); }
    finally { setLoadingDataSummary(false); }
  };

  const handleDownloadAllData = async () => {
    setDownloadingData(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/gdpr/download-my-data`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nutristeps_data_export_${new Date().toISOString().slice(0,10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded!');
    } catch (err) {
      toast.error('Failed to download data: ' + (err.message || 'Unknown error'));
    } finally { setDownloadingData(false); }
  };

  const handleDownloadGdprPdf = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/gdpr/privacy-policy/download`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'NutriSteps_GDPR_Privacy_Policy.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Privacy Policy downloaded!');
    } catch (err) {
      toast.error('Failed to download: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEmailGdprPolicy = async () => {
    setEmailingPolicy(true);
    try {
      const result = await api.post('/gdpr/privacy-policy/email', {});
      toast.success(result.message || 'Privacy Policy sent to your email!');
    } catch (err) {
      toast.error('Failed to send email: ' + (err.message || 'Unknown error'));
    } finally { setEmailingPolicy(false); }
  };

  const handleDeleteAllData = async () => {
    if (!window.confirm('WARNING: This will permanently delete ALL your data!\n\nThis includes:\n- All nutrition logs\n- All walking data\n- All AI plans\n- All saved meals and products\n- All progress photos\n- All body measurements\n\nThis action CANNOT be undone.\n\nAre you sure you want to continue?')) return;
    const confirmText = window.prompt('To confirm deletion, type "DELETE MY DATA":');
    if (confirmText !== 'DELETE MY DATA') {
      toast.error('Deletion cancelled - confirmation text did not match');
      return;
    }
    setDeletingData(true);
    try {
      await api.delete('/gdpr/delete-my-data');
      toast.success('All your data has been deleted');
      localStorage.clear();
      setTimeout(() => { window.location.reload(); }, 2000);
    } catch (err) {
      toast.error('Failed to delete data: ' + (err.message || 'Unknown error'));
    } finally { setDeletingData(false); }
  };

  // Payment history functions
  const loadPaymentHistory = async () => {
    setLoadingHistory(true);
    try {
      const data = await api.getPaymentHistory();
      setPaymentHistory(data.payments || []);
    } catch (err) { toast.error('Failed to load payment history'); }
    finally { setLoadingHistory(false); }
  };

  const handleDownloadInvoice = async (paymentId) => {
    setDownloadingInvoice(paymentId);
    try {
      const blob = await api.downloadInvoice(paymentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Invoice downloaded');
    } catch (err) { toast.error('Failed to download invoice'); }
    finally { setDownloadingInvoice(null); }
  };

  return {
    // Backup
    creatingBackup, lastBackup,
    showRestoreBackup, setShowRestoreBackup,
    backupList, loadingBackups,
    restoringBackup, handleCreateBackup,
    handleRestoreBackup, handleRestoreFromS3,
    // GDPR
    downloadingData, emailingPolicy, deletingData,
    dataSummary, loadingDataSummary,
    loadDataSummary, handleDownloadAllData,
    handleDownloadGdprPdf, handleEmailGdprPolicy,
    handleDeleteAllData,
    // Mealtimes
    userMealTimes, setUserMealTimes,
    customMealName, setCustomMealName,
    savingMealTimes, setSavingMealTimes,
    // Payment
    paymentHistory, loadingHistory,
    downloadingInvoice, loadPaymentHistory,
    handleDownloadInvoice,
  };
}
